/**
 * The Inbox's shapes — `docs/activity.md` §7, §9.3, §9.4.
 *
 * One conversation per customer, texts and email together. The request
 * schemas here are what the New sheets and the composer send; the response
 * types are what `GET /conversations` and `GET /conversations/:id` answer.
 */
import { z } from 'zod';
import { recordRefSchema } from '../records.js';
/** Photos in a text: images only, at most 5, 5 MB together (§7.5). */
export const MMS_MAX_FILES = 5;
export const MMS_MAX_BYTES = 5 * 1024 * 1024;
/** Email attachments, together (§7.6). */
export const EMAIL_MAX_BYTES = 25 * 1024 * 1024;
/**
 * Email attachments together **while Postmark sends Rowted's email** (Aaron,
 * Oct 26): Postmark refuses a message over 10 MB once its files are base64'd
 * inside it, which leaves about 7 MB of files with room for the words. A
 * person's own Outlook keeps `EMAIL_MAX_BYTES`.
 */
export const POSTMARK_EMAIL_MAX_BYTES = 7_000_000;
/**
 * Files on one email together **while Postmark sends** (Aaron, Oct 27,
 * answer 16: "build it"): what fits under Postmark's 10 MB goes attached and
 * the rest as download links (`email-file-links.ts`), so the limit is no
 * longer Postmark's but ours — 100 MB, four of the biggest uploads a phone
 * makes, and enough that one email is never a way to hand out a folder.
 */
export const POSTMARK_LINKED_MAX_BYTES = 100 * 1024 * 1024;
/** How long a file's download link in an email works (answer 16). */
export const EMAIL_FILE_LINK_DAYS = 30;
/**
 * *Attachments can be up to 25 MB together.* — the one sentence for whichever
 * limit applies; while Postmark sends it says that big files go as links.
 */
export const emailFilesWords = (bytes) => (bytes === POSTMARK_LINKED_MAX_BYTES
    ? `Files can be up to ${Math.round(bytes / (1024 * 1024))} MB together. Past about `
        + `${Math.round(POSTMARK_EMAIL_MAX_BYTES / 1_000_000)} MB, the biggest go as download links that work for `
        + `${EMAIL_FILE_LINK_DAYS} days.`
    : `Attachments can be up to ${Math.round(bytes / (1024 * 1024))} MB together.`);
/**
 * Under the composer's files while Postmark sends and they come to more than
 * fits attached: what will happen, before it does.
 */
export const emailLinksNote = () => `Over about ${Math.round(POSTMARK_EMAIL_MAX_BYTES / 1_000_000)} MB together, so the biggest go as download links `
    + `that work for ${EMAIL_FILE_LINK_DAYS} days.`;
/** How long a photo's link in a text stays good (§7.5). */
export const MMS_LINK_SECONDS = 3600;
export const INBOX_CHANNELS = ['sms', 'email'];
/** *Attach it to a contact, property or job.* — a text, an email or a note with nothing attached (§9.2, T39). */
export const ATTACH_SOMETHING = 'Attach it to a contact, property or job.';
/** *Renee has stopped texts from you* (§7.5). */
export const stoppedTextsWords = (firstName) => `${firstName} has stopped texts from you`;
const body = z.string().trim().min(1, 'Write something first.').max(10_000);
const address = z.string().trim().email().max(320);
/**
 * The most an email may go to, To, Cc and Bcc together (Aaron, Oct 26): the
 * limit Postmark refuses past, kept on every way out so a message never
 * depends on which service sends it.
 */
export const EMAIL_MAX_RECIPIENTS = 50;
/** *An email can go to 50 people at most — To, Cc and Bcc together.* */
export const TOO_MANY_RECIPIENTS = `An email can go to ${EMAIL_MAX_RECIPIENTS} people at most — To, Cc and Bcc together.`;
/** People in the company copied on one email, in one box. */
export const EMAIL_MAX_TEAMMATES = 25;
/** A reply in a conversation (§7.5–§7.7). */
export const messageInputSchema = z.object({
    channel: z.enum(INBOX_CHANNELS),
    body,
    /** Email only. Defaults to *Re: <the last subject>*. */
    subject: z.string().trim().max(300).optional(),
    /** Email only: addresses typed into *Cc* and *Bcc* (Aaron, Oct 26: "multiple email addresses"). */
    cc: z.array(address).max(EMAIL_MAX_RECIPIENTS - 1).optional(),
    bcc: z.array(address).max(EMAIL_MAX_RECIPIENTS - 1).optional(),
    /**
     * Email only: people in the company picked into *Cc* or *Bcc* (Aaron, Oct
     * 26) — by id, so their own address is the server's to know. Each follows
     * the conversation from then on (it is in their Inbox) and gets the email
     * at that address, in the box they were picked into.
     */
    ccPeople: z.array(z.string().uuid()).max(EMAIL_MAX_TEAMMATES).optional(),
    bccPeople: z.array(z.string().uuid()).max(EMAIL_MAX_TEAMMATES).optional(),
    /** Files already uploaded: photos for a text, attachments for an email. */
    fileIds: z.array(z.string().uuid()).max(20).optional(),
    /** For a text: which of the contact's mobiles, when there are several. */
    to: z.string().trim().max(320).optional(),
});
/** New text and New email (§9.3, §9.4): to a contact, attached to at least one record. */
export const newConversationSchema = messageInputSchema.extend({
    contactId: z.string().uuid(),
    links: z.array(recordRefSchema).max(10).default([]),
    /**
     * The record page it was written from (Aaron, Oct 23 — a record's Email
     * and Text send through here): the message is filed on that record, so its
     * own Activity shows it whatever else the conversation is attached to.
     */
    origin: recordRefSchema.optional(),
});
export const conversationLinksSchema = z.object({
    links: z.array(recordRefSchema).max(10),
});
/**
 * The Inbox's three views (§7.1, §7.10): **Unread**, **All** — neither shows
 * what the viewer archived — and **Archived** (Aaron, Oct 24, note 2.3).
 */
export const INBOX_FILTERS = ['unread', 'all', 'archived'];
export const inboxCategorySchema = z.object({
    name: z.string().max(80),
    colour: z.string().max(20),
});
export const inboxCategoryPatchSchema = inboxCategorySchema.partial();
/** The categories a conversation wears for the viewer — the whole set, never a delta. */
export const conversationCategoriesSchema = z.object({
    categoryIds: z.array(z.string().uuid()).max(30),
});
//# sourceMappingURL=inbox.js.map